import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Dropdown, Form } from "semantic-ui-react";
import CampaignsService from "../../../../services/campaign";

const CampaignSelector = ({
  advertiserId = null,
  value = null,
  onChange,
  disabled = false,
  error = false,
  errorMessage = null,
  required = false,
  hasLucid = false,
  onCampaignSelect,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const service = useRef(new CampaignsService());
  const campaignDataRef = useRef(new Map());
  const previousAdvertiserId = useRef(advertiserId);
  const intl = useIntl();
  // Show a warning if the user has Lucid enabled but no campaigns are returned,
  // which likely means the selected advertiser does not have campaigns with a lucid tag.
  const showNoLucidTagCampaignsWarning = hasLucid && options.length === 0 && Boolean(advertiserId) && !loading;

  // biome-ignore lint/correctness/useExhaustiveDependencies: onChange excluded intentionally
  useEffect(() => {
    const hasPreviousAdvertiser = previousAdvertiserId.current !== null && previousAdvertiserId.current !== undefined;
    const hasAdvertiserChanged = hasPreviousAdvertiser && previousAdvertiserId.current !== advertiserId;
    setOptions([]);
    campaignDataRef.current = new Map();
    setFetchError(false);
    if (hasAdvertiserChanged) {
      onChange(null, { name: "campaign_id", value: null });
    }

    if (!advertiserId) return;
    previousAdvertiserId.current = advertiserId;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const response = await service.current.getBrandLiftCampaigns(advertiserId, false, hasLucid);
        if (!cancelled) {
          campaignDataRef.current = new Map(response.data.map((c) => [c.id, c]));
          setOptions(
            response.data.map(({ id, title }) => ({
              key: id,
              text: title,
              value: id,
            })),
          );
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [advertiserId, hasLucid]);

  const handleChange = (e, data) => {
    onChange(e, data);
    if (onCampaignSelect) {
      onCampaignSelect(data.value ? campaignDataRef.current.get(data.value) ?? null : null);
    }
  };

  return (
    <Form.Field
      inline
      error={error}
      required={required}
    >
      <label>
        {intl.formatMessage({
          id: "LABEL_CAMPAIGN",
          defaultMessage: "Campaign",
        })}
      </label>
      <Dropdown
        aria-label="Campaign"
        clearable
        disabled={disabled || !advertiserId}
        loading={loading}
        name="campaign_id"
        onChange={handleChange}
        options={options}
        placeholder={intl.formatMessage({
          id: "HINT_CAMPAIGN",
          defaultMessage: "Select campaign",
        })}
        search
        selection
        value={value}
      />
      {errorMessage && <div className="custom-error">{errorMessage}</div>}
      {fetchError && (
        <div className="custom-error">
          {intl.formatMessage({
            id: "ERROR_CAMPAIGN_FETCH_FAILED",
            defaultMessage: "Failed to load campaigns. Please try again.",
          })}
        </div>
      )}
      {showNoLucidTagCampaignsWarning && (
        <div
          style={{
            fontStyle: "italic",
            marginTop: "0.5rem",
          }}
        >
          {intl.formatMessage({
            id: "WARNING_NO_LUCID_TAG_CAMPAIGNS",
            defaultMessage: "No campaigns with a Lucid tag are available under the selected advertiser.",
          })}
        </div>
      )}
    </Form.Field>
  );
};

CampaignSelector.propTypes = {
  advertiserId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  hasLucid: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onCampaignSelect: PropTypes.func,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
};

export default CampaignSelector;
